import { OmniElement, html, css } from 'omni-ui';

import './campaign-thought-leader-step-one.js';
import '../campaign-pinnable-cards.js';

import { produce } from 'immer';

export class CampaignThoughtLeader extends OmniElement {
  static get styles() {
    return [
      ...super.styles,
      css`
        .thought-leader-wrapper {
          display: flex;
          flex-direction: column;
        }
      `,
    ];
  }

  /**
   * CampaignThoughtLeader properties
   * @property {Object} context - Values passed in from wrapper/router
   * @property {Object} stepOne - Saved data from thought leader step one module
   * @property {Object} stepTwo - Saved data from thought leader step two module
   */
  static get properties() {
    return {
      context: { type: Object },
      stepOne: { type: Object },
      stepTwo: { type: Object },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.stepOne = this.savedData?.stepOne ?? {};
    this.stepTwo = this.savedData?.stepTwo ?? {};
  }

  /**
   * Handles the change event for thought leader step one module child component
   * @param {CustomEvent} e - The change event object that contains the output, areas, and textarea values to be saved
   */
  #handleChangeStep1 = e => {
    const {
      detail: { output, areas, textarea },
    } = e ?? this.stepOne;

    if (e.detail?.output || e.detail?.areas) {
      this.stepOne = produce(this.stepOne, draft => {
        draft.output = output;
        draft.areas = areas;
        draft.textarea = textarea;
      });
    }
    this.#dispatchModuleChange();
  };

  /**
   * Handles the change event for thought leader step two module child component
   * @param {CustomEvent} e - The change event object that contains the thoughtLeaders object to be saved
   */
  #handleChangeStep2 = e => {
    const {
      detail: { cards },
    } = e ?? this.stepTwo;

    if (cards) {
      this.stepTwo = produce(this.stepTwo, draft => {
        draft.thoughtLeaders = cards;
      });
    }
    this.#dispatchModuleChange();
  };

  /**
   * @fires OmniCampaignThoughtLeader~omni-module-changed
   */
  #dispatchModuleChange = async () => {
    await this.updateComplete;

    const { uuid_module: moduleId } = this.context;
    const { stepOne, stepTwo } = this;

    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: () =>
          this.save(
            { stepOne, stepTwo },
            {
              '/studio/thoughtleader':
                stepTwo?.thoughtLeaders?.find(x => x.isPinned) ?? null,
            },
          ),
      },
    });
  };

  /**
   * This is a helper function for expanding thoughtleader-specific template strings that exist in assist prompts.
   *
   * @param {String[]} templates - list of template strings to expand
   * @returns {String[]} The expanded list of templates (undefined when not found)
   *
   */
  #assistTemplateContext = templates =>
    templates.map(template => {
      if (template === 'needStates')
        return (
          this.stepOne.areas?.needStates?.map(x => x.text)?.join(', ') ||
          undefined
        );
      if (template === 'assets')
        return (
          this.stepOne.areas?.messages?.map(x => x.text)?.join(', ') ||
          undefined
        );
      if (template === 'widerContext')
        return this.stepOne.textarea || undefined;
      return undefined;
    });

  render() {
    return html`
      <omni-style>
        <div class="thought-leader-wrapper">
          <campaign-thought-leader-step-one
            .moduleTitle=${this.moduleTitle}
            .savedData=${this.stepOne}
            .context=${this.context}
            .schema=${this.schema}
            .assistTemplateContext=${this.#assistTemplateContext}
            @thought-change=${this.#handleChangeStep1}
          >
          </campaign-thought-leader-step-one>
          <campaign-pinnable-cards
            .cards=${this.stepTwo?.thoughtLeaders ?? []}
            .env=${this.env}
            .token=${this.token}
            .context=${this.context}
            .schema=${this.schema}
            .assistFieldId=${'thoughtLeader'}
            .assistTemplateContext=${this.#assistTemplateContext}
            @card-change=${this.#handleChangeStep2}
          >
          </campaign-pinnable-cards>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-thought-leader', CampaignThoughtLeader);
